import RoomIcon from "@mui/icons-material/Room";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip as MuiTooltip,
  Typography,
  useTheme,
} from "@mui/material";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Marker,
  MapContainer,
  TileLayer,
  Tooltip,
  Popup,
  Polyline,
  ZoomControl,
  useMap,
} from "react-leaflet";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";

// Fixed Area46 coordinates — routes/travel-time are drawn from here to each venue.
const HQ = { lat: -17.7798269, lng: 31.0546921 };
const HQ_COLOR = "#f59e0b";

interface ActiveRoute {
  venueId: string;
  coordinates: [number, number][];
  distanceKm: number;
  durationMin: number;
}

interface RouteFromArea46Response {
  distance_km: number;
  duration_minutes: number;
  geometry: [number, number][];
  cached: boolean;
}

export interface MapVenueItem {
  id: string;
  name: string;
  lat: number;
  lng: number;
}

interface VenueMapApiItem {
  id: number;
  name: string;
  lat: number;
  lng: number;
}

interface VenueMapApiResponse {
  count: number;
  results: VenueMapApiItem[];
}

const MARKER_COLOR = "#3a86ff";
const FALLBACK_CENTER: [number, number] = [-17.779922, 31.057847];

const venueIcon = L.divIcon({
  className: "",
  html: `
    <div style="position:relative;width:26px;height:34px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));">
      <svg width="26" height="34" viewBox="0 0 26 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13 0C5.8 0 0 5.8 0 13c0 9.75 13 21 13 21s13-11.25 13-21c0-7.2-5.8-13-13-13z" fill="${MARKER_COLOR}"/>
        <circle cx="13" cy="13" r="5.5" fill="#ffffff"/>
      </svg>
    </div>
  `,
  iconSize: [26, 34],
  iconAnchor: [13, 34],
  popupAnchor: [0, -32],
  tooltipAnchor: [0, -28],
});

const hqIcon = L.divIcon({
  className: "",
  html: `
    <div style="position:relative;width:30px;height:38px;filter:drop-shadow(0 2px 5px rgba(0,0,0,0.4));">
      <svg width="30" height="38" viewBox="0 0 30 38" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 11.25 15 23 15 23s15-11.75 15-23c0-8.3-6.7-15-15-15z" fill="${HQ_COLOR}"/>
        <circle cx="15" cy="15" r="7" fill="#ffffff"/>
      </svg>
    </div>
  `,
  iconSize: [30, 38],
  iconAnchor: [15, 38],
  popupAnchor: [0, -36],
  tooltipAnchor: [0, -32],
});

function FitToVenues({ venues }: { venues: MapVenueItem[] }) {
  const map = useMap();

  useEffect(() => {
    if (!venues.length) return;
    const bounds = L.latLngBounds([
      [HQ.lat, HQ.lng],
      ...venues.map((v): [number, number] => [v.lat, v.lng]),
    ]);
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues]);

  return null;
}

function RecenterControl({ venues }: { venues: MapVenueItem[] }) {
  const map = useMap();

  const handleRecenter = () => {
    const bounds = L.latLngBounds([
      [HQ.lat, HQ.lng],
      ...venues.map((v): [number, number] => [v.lat, v.lng]),
    ]);
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 15 });
  };

  return (
    <Box sx={{ position: "absolute", bottom: 16, right: 16, zIndex: 1000 }}>
      <MuiTooltip title="Recenter map">
        <IconButton
          onClick={handleRecenter}
          size="small"
          sx={{
            bgcolor: "#ffffff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            "&:hover": { bgcolor: "#f3f4f6" },
          }}
        >
          <MyLocationRoundedIcon sx={{ fontSize: 18, color: "#1f2937" }} />
        </IconButton>
      </MuiTooltip>
    </Box>
  );
}

export default function OperationsMap() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [venues, setVenues] = useState<MapVenueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeRoute, setActiveRoute] = useState<ActiveRoute | null>(null);
  const [routeLoadingVenueId, setRouteLoadingVenueId] = useState<
    string | null
  >(null);
  const [routeErrorVenueId, setRouteErrorVenueId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    apiService
      .get<VenueMapApiResponse>("/api/management/dashboard/venues-map/")
      .then((res) => {
        setVenues(
          res.data.results.map((venue) => ({
            id: String(venue.id),
            name: venue.name,
            lat: venue.lat,
            lng: venue.lng,
          })),
        );
      })
      .catch(() => setVenues([]))
      .finally(() => setLoading(false));
  }, []);

  const handleShowRoute = async (venue: MapVenueItem) => {
    setRouteErrorVenueId(null);
    setRouteLoadingVenueId(venue.id);
    try {
      const res = await apiService.get<RouteFromArea46Response>(
        `/api/venues/venue/${venue.id}/route-from-area46/`,
      );
      setActiveRoute({
        venueId: venue.id,
        coordinates: res.data.geometry,
        distanceKm: res.data.distance_km,
        durationMin: res.data.duration_minutes,
      });
    } catch {
      setRouteErrorVenueId(venue.id);
    } finally {
      setRouteLoadingVenueId(null);
    }
  };

  const handleHideRoute = () => {
    setActiveRoute(null);
    setRouteErrorVenueId(null);
  };

  const initialCenter = useMemo((): [number, number] => {
    if (!venues.length) return FALLBACK_CENTER;
    const avgLat = venues.reduce((sum, v) => sum + v.lat, 0) / venues.length;
    const avgLng = venues.reduce((sum, v) => sum + v.lng, 0) / venues.length;
    return [avgLat, avgLng];
  }, [venues]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "24px",
        backgroundColor: colors.primary[900],
        width: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: `${MARKER_COLOR}1A`,
            }}
          >
            <RoomIcon sx={{ color: MARKER_COLOR, fontSize: 20 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} color={colors.primary[100]}>
            Operations Map
          </Typography>
        </Box>

        <Chip
          label={loading ? "Loading…" : `${venues.length} Venue${venues.length === 1 ? "" : "s"}`}
          size="small"
          sx={{
            bgcolor: `${MARKER_COLOR}1A`,
            color: MARKER_COLOR,
            fontWeight: 700,
            borderRadius: "20px",
            px: 0.5,
          }}
        />
      </Box>

      {/* Map */}
      <Box
        sx={{
          height: "360px",
          width: "100%",
          borderRadius: "16px",
          overflow: "hidden",
          position: "relative",
          backgroundColor: colors.primary[800],
          border: `1px solid ${colors.primary[800]}`,
          "& .leaflet-popup-content-wrapper": {
            borderRadius: "12px",
            padding: 0,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          },
          "& .leaflet-popup-content": {
            margin: 0,
            minWidth: "180px",
          },
          "& .leaflet-popup-tip": {
            boxShadow: "none",
          },
          "& .leaflet-tooltip": {
            borderRadius: "8px",
            border: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            fontWeight: 600,
            fontSize: "0.75rem",
            padding: "4px 10px",
          },
          "& .leaflet-control-zoom": {
            border: "none",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            borderRadius: "10px !important",
            overflow: "hidden",
          },
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 2,
            }}
          >
            <CircularProgress size={36} sx={{ color: MARKER_COLOR }} />
            <Typography variant="body2" color={colors.primary[300]}>
              Loading venues...
            </Typography>
          </Box>
        ) : venues.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1,
              px: 3,
              textAlign: "center",
            }}
          >
            <RoomIcon sx={{ fontSize: 32, color: colors.primary[400] }} />
            <Typography variant="body2" fontWeight={700} color={colors.primary[300]}>
              No venues on the map yet
            </Typography>
            <Typography variant="caption" color={colors.primary[400]}>
              Venues will appear here once they have coordinates set.
            </Typography>
          </Box>
        ) : (
          <MapContainer
            center={initialCenter}
            zoom={13}
            zoomControl={false}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
            <ZoomControl position="bottomleft" />
            <FitToVenues venues={venues} />
            <RecenterControl venues={venues} />

            {activeRoute && (
              <Polyline
                positions={activeRoute.coordinates}
                pathOptions={{
                  color: MARKER_COLOR,
                  weight: 4,
                  opacity: 0.85,
                }}
              />
            )}

            <Marker position={[HQ.lat, HQ.lng]} icon={hqIcon}>
              <Tooltip direction="top" offset={[0, -34]} opacity={1}>
                Area46
              </Tooltip>
              <Popup>
                <Box sx={{ p: 1.5, minWidth: 160 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#1f2937" }}>
                    Area46
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#6b7280" }}>
                    {HQ.lat.toFixed(5)}, {HQ.lng.toFixed(5)}
                  </Typography>
                </Box>
              </Popup>
            </Marker>

            {venues.map((venue) => {
              const isActiveRoute = activeRoute?.venueId === venue.id;
              const isRouteLoading = routeLoadingVenueId === venue.id;
              return (
                <Marker key={venue.id} position={[venue.lat, venue.lng]} icon={venueIcon}>
                  <Tooltip direction="top" offset={[0, -30]} opacity={1}>
                    {venue.name}
                  </Tooltip>
                  <Popup>
                    <Box sx={{ p: 1.5, minWidth: 200 }}>
                      <Typography
                        variant="subtitle2"
                        component={RouterLink}
                        to={`/venues/view/${venue.id}`}
                        fontWeight={700}
                        sx={{
                          color: MARKER_COLOR,
                          mb: 0.5,
                          display: "block",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {venue.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b7280", display: "block", mb: 1 }}>
                        {venue.lat.toFixed(5)}, {venue.lng.toFixed(5)}
                      </Typography>

                      {isActiveRoute && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            mb: 1,
                            p: 0.75,
                            borderRadius: "8px",
                            bgcolor: `${MARKER_COLOR}14`,
                          }}
                        >
                          <DirectionsCarRoundedIcon sx={{ fontSize: 16, color: MARKER_COLOR }} />
                          <Typography variant="caption" fontWeight={700} sx={{ color: "#1f2937" }}>
                            {activeRoute.distanceKm.toFixed(1)} km · ~{Math.round(activeRoute.durationMin)} min from Area46
                          </Typography>
                        </Box>
                      )}

                      {routeErrorVenueId === venue.id && (
                        <Typography variant="caption" sx={{ color: "#dc2626", display: "block", mb: 1 }}>
                          Could not calculate a route to this venue. Please try again.
                        </Typography>
                      )}

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          variant={isActiveRoute ? "outlined" : "contained"}
                          disableElevation
                          disabled={isRouteLoading}
                          onClick={() =>
                            isActiveRoute ? handleHideRoute() : handleShowRoute(venue)
                          }
                          startIcon={
                            isRouteLoading ? (
                              <CircularProgress size={12} color="inherit" />
                            ) : isActiveRoute ? (
                              <CloseRoundedIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <DirectionsCarRoundedIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          sx={{
                            fontSize: "0.7rem",
                            textTransform: "none",
                            borderRadius: "8px",
                            bgcolor: isActiveRoute ? "transparent" : MARKER_COLOR,
                            borderColor: MARKER_COLOR,
                            color: isActiveRoute ? MARKER_COLOR : "#ffffff",
                            "&:hover": {
                              bgcolor: isActiveRoute ? `${MARKER_COLOR}14` : MARKER_COLOR,
                            },
                          }}
                        >
                          {isRouteLoading
                            ? "Calculating..."
                            : isActiveRoute
                              ? "Hide route"
                              : "Route from Area46"}
                        </Button>

                        <Box
                          component="a"
                          href={`https://www.google.com/maps/search/?api=1&query=${venue.lat},${venue.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            color: "#6b7280",
                            textDecoration: "none",
                          }}
                        >
                          Open in Maps
                          <OpenInNewRoundedIcon sx={{ fontSize: 12 }} />
                        </Box>
                      </Box>
                    </Box>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </Box>
    </Paper>
  );
}
